import { createFileRoute } from "@tanstack/react-router";
import { pageHead } from "@/lib/seo";
import { PageLayout, PageSection } from "@/components/site/PageLayout";
import { Reveal, Label } from "@/components/site/Reveal";
import { EngineeredGoodForm } from "@/components/site/EngineeredGoodForm";
import { Toaster } from "@/components/ui/sonner";
import heroEngineeredGoodAsset from "@/assets/swim-thumbs-up.jpg.asset.json";
const heroEngineeredGood = heroEngineeredGoodAsset.url;

// Published by FSPA for its Florida Swims Foundation (2023). FSPA's figures, not SunSmart's.
const FSPA_FOUNDATION_SOURCE = "https://fspa.com/safety/florida-swims-foundation/";
const FSPA_FOUNDATION_STATS = [
  { value: "$200,000+", label: "Raised for swim safety" },
  { value: "11,045", label: "Swim lessons provided" },
  { value: "3,892", label: "Children reached" },
];

export const Route = createFileRoute("/engineered-good")({
  head: () => ({
    ...pageHead({
      title: "Engineering Good Giving Program · SunSmart Engineering",
      description:
        "Engineering Good turns each contract into a contribution: a share of profits from partner projects goes to trade-organization charities.",
      path: "/engineered-good",
    }),
  }),
  component: EngineeredGoodPage,
});

function EngineeredGoodPage() {
  return (
    <PageLayout
      kicker="Impact Through Partnership"
      title="Engineering Good."
      intro="We don't just design systems, we invest in the people and organizations that move our industries forward. Through our Engineering Good initiative, we transform every contract into a contribution for a cause that matters to you."
    >
      {/* Toaster mounted locally since __root.tsx does not render one globally. */}
      <Toaster />
      <PageSection label="01" title="How It Works">
        <Reveal>
          <p className="max-w-2xl text-lg leading-relaxed text-muted-foreground">
            We partner with major trade organizations to support their philanthropic missions. When a member
            of a participating organization partners with SunSmart Engineering for their project, we donate a
            percentage of our profits directly to that organization's designated charity.
          </p>
          <p className="mt-4 max-w-2xl text-lg leading-relaxed text-muted-foreground">
            This model ensures that our growth directly fuels the safety, education, and health initiatives
            championed by your professional community.
          </p>
        </Reveal>
      </PageSection>

      <PageSection theme="surface">
        <div className="grid gap-16 lg:grid-cols-12 lg:items-start">
          <Reveal className="lg:col-span-5">
            <div className="overflow-hidden rounded-sm border border-border aspect-[3/4]">
              <img
                src={heroEngineeredGood}
                alt="Child underwater giving a thumbs up during a swim lesson"
                loading="lazy"
                className="h-full w-full object-cover"
              />
            </div>
          </Reveal>
          <div className="lg:col-span-7">
            <Reveal delay={80}>
              <Label n="02" text="Featured Partner" />
              <h2 className="mt-6 font-display text-[clamp(2rem,4.5vw,3.5rem)] font-black uppercase leading-[0.95]">
                FSPA & <span className="text-sun">Every Child A Swimmer</span>
              </h2>
            </Reveal>
            <Reveal delay={160}>
              <p className="mt-6 text-lg leading-relaxed text-muted-foreground">
                For every project we complete for FSPA members, a portion of the proceeds is donated to Every
                Child A Swimmer, resulting in over one hundred free lessons each quarter.
              </p>
              <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
                Drowning remains a leading cause of accidental death for children. Every Child A Swimmer works
                to eliminate these preventable tragedies by providing scholarships for life-saving swim
                lessons to children in need.
              </p>
              <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
                Our partnerships have already funded real work in the community. We believe that professional growth
                should directly fuel community safety, and these contributions represent a significant, ongoing
                investment in local families.
              </p>
            </Reveal>

            <div className="mt-8 grid gap-px border border-border bg-border sm:grid-cols-2">
              <Reveal delay={200}>
                <div className="h-full bg-background p-6 md:p-8">
                  <div className="font-display text-4xl font-black leading-none text-sun">$35</div>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                    Provides a child with a critical life-saving swim lesson.
                  </p>
                </div>
              </Reveal>
              <Reveal delay={260}>
                <div className="h-full bg-background p-6 md:p-8">
                  <div className="font-display text-4xl font-black leading-none text-sun">$180</div>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                    Sponsors a full course, giving a child the skills and confidence to stay safe in the water.
                  </p>
                </div>
              </Reveal>
            </div>

            <Reveal delay={320}>
              <p className="mt-8 text-lg leading-relaxed text-muted-foreground">
                We aren't just engineering better systems; we are securing the future of our communities. This
                initiative ensures the next generation can enjoy the water safely, one lesson and one project
                at a time.
              </p>
            </Reveal>
          </div>
        </div>

        <div className="mt-20 border-t border-border pt-12 md:mt-24 md:pt-16">
          <Reveal>
            <div className="grid gap-6 lg:grid-cols-12 lg:items-end">
              <div className="lg:col-span-7">
                <div className="font-mono text-[0.7rem] uppercase tracking-[0.22em] text-muted-foreground">
                  <span className="text-sun">FSPA</span> · Florida Swims Foundation · 2023
                </div>
                <h3 className="mt-4 font-display text-[clamp(1.5rem,3vw,2.25rem)] font-black uppercase leading-[0.95]">
                  What FSPA's charity did in one year
                </h3>
              </div>
              <p className="max-w-[52ch] text-pretty text-base leading-relaxed text-muted-foreground lg:col-span-5">
                The Florida Swims Foundation is FSPA's 501(c)(3). It funds swim lessons and water-safety
                education across Florida, including grants through Every Child A Swimmer.
              </p>
            </div>
          </Reveal>
          <dl className="mt-10 grid gap-px border border-border bg-border lg:grid-cols-3">
            {FSPA_FOUNDATION_STATS.map((stat, i) => (
              <Reveal
                key={stat.label}
                delay={i * 80}
                className="flex h-full flex-col-reverse justify-end gap-3 bg-background p-6 md:p-8"
              >
                <dt className="text-base text-muted-foreground">{stat.label}</dt>
                <dd className="font-display text-[clamp(2.5rem,4vw,4.5rem)] font-black leading-none text-sun tabular-nums">
                  {stat.value}
                </dd>
              </Reveal>
            ))}
          </dl>
          <p className="mt-4 text-sm text-muted-foreground">
            Figures published by{" "}
            <a
              href={FSPA_FOUNDATION_SOURCE}
              target="_blank"
              rel="noopener noreferrer"
              className="text-foreground underline decoration-sun/60 underline-offset-4 hover:text-sun"
            >
              FSPA
            </a>{" "}
            for the foundation's full 2023 work, not SunSmart's share alone.
          </p>
        </div>
      </PageSection>

      <PageSection>
        <div id="partner" className="grid scroll-mt-28 gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-32">
              <Reveal>
                <Label n="03" text="Partner With Us" />
                <h2 className="mt-6 font-display text-[clamp(2rem,4.5vw,3.5rem)] font-black uppercase leading-[0.95]">
                  Have a charity or initiative <span className="text-sun">to support?</span>
                </h2>
              </Reveal>
              <Reveal delay={80}>
                <p className="mt-6 max-w-[48ch] text-pretty text-lg leading-relaxed text-muted-foreground">
                  Tell us who you are and the cause your members care about. If it's a fit, we'll build an
                  Engineering Good partnership around it.
                </p>
              </Reveal>
              <Reveal delay={160}>
                <ol className="mt-10 grid gap-px border border-border bg-border">
                  {[
                    ["Your note goes straight to our team."],
                    ["We set up a short call to learn about your organization and its cause."],
                    ["Together we decide how your members' projects give back to it."],
                  ].map(([step], i) => (
                    <li key={step} className="flex gap-5 bg-background p-5 md:p-6">
                      <span className="font-mono text-[0.7rem] tracking-[0.22em] text-sun">0{i + 1}</span>
                      <span className="text-base leading-relaxed text-foreground">{step}</span>
                    </li>
                  ))}
                </ol>
              </Reveal>
              <Reveal delay={220}>
                <div className="mt-8 grid text-sm leading-relaxed text-muted-foreground">
                  <span>Rather talk now?</span>
                  <a
                    href="tel:8667868655"
                    className="w-fit py-2 text-foreground underline decoration-sun/60 underline-offset-4 hover:text-sun"
                  >
                    866-SUN-8655
                  </a>
                  <a
                    href="mailto:solutions@sunsmartengineering.com"
                    className="w-fit py-2 text-foreground underline decoration-sun/60 underline-offset-4 hover:text-sun"
                  >
                    solutions@sunsmartengineering.com
                  </a>
                </div>
              </Reveal>
            </div>
          </div>
          <Reveal delay={120} className="lg:col-span-7">
            <div className="rounded-sm border border-border bg-surface p-6 md:p-10">
              <EngineeredGoodForm />
            </div>
          </Reveal>
        </div>
      </PageSection>
    </PageLayout>
  );
}
